import { MutableRefObject, createContext, useContext, useMemo } from "react";
import { CalendarProps, ToolbarProps } from "react-big-calendar";
import styled from "styled-components";
import invariant from "tiny-invariant";
import { CalendarEvent } from "@lifeat/shared/calendar/impl/CalendarEvent";
import { CalendarList } from "@lifeat/shared/calendar/types/CalendarList";
import { CalendarDetails } from "@lifeat/shared/data-models/calendar/CalendarDetails";
import {
    CalendarConnection,
    CalendarProvider,
} from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import ExternalLinkIconSvg from "@icons/ExternalLinkIcon.svg";
import { FontSize } from "@themes/Fonts";
import { BrowserUtil } from "../../../common/util";
import { OpenCalendarLinkEvent } from "../AmplitudeCalendarEvents";
import CalendarSelector from "./CalendarSelector";
import CalendarSettingsModal, { CalendarSettingsModalHandle } from "./CalendarSettingsModal";
import GoogleCalendarAuthButton from "./GoogleCalendar/GoogleCalendarAuthButton";
import OutlookCalendarAuthButton from "./OutlookCalendar/OutlookCalendarAuthButton";
import ChevronLeftIcon from "/resources/icons/ChevronLeftArrow.svg";
import ChevronRightIcon from "/resources/icons/ChevronRightArrow.svg";

const TODAY_BUTTON_TEXT = "Today";

const ToolbarRow = styled.div<{ isCondensed?: boolean }>`
    display: flex;
    align-items: center;
    padding: 15px;

    justify-content: ${(props) => (props.isCondensed ? `center` : `space-between`)};
    gap: 15px;
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const TodayButton = styled(LifeAtButton)`
    border: 1px solid ${(props) => props.theme.borderPrimary};

    button {
        font-size: ${FontSize.small};
    }

    padding: 5px;
    min-height: unset;
`;

const DateText = styled.span`
    white-space: nowrap;
    font-size: ${FontSize.normal};
`;

const ExternalLinkIcon = styled(ExternalLinkIconSvg)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

interface CalendarToolbarProps {
    /**
     * List of selected calendars
     */
    readonly selectedCalendarList: CalendarDetails[];
    /**
     * List of calendar details
     */
    readonly calendars: CalendarList;
    readonly onSelectedCalendarChange: (calendar: CalendarDetails, selected: boolean) => void;
    readonly onCalendarConnectionRemove: (connection: CalendarConnection) => void;
    readonly settingsModalRef: MutableRefObject<CalendarSettingsModalHandle>;
}

export const CalendarToolbarPropsContext = createContext<CalendarToolbarProps>({
    selectedCalendarList: [],
    calendars: {
        [CalendarProvider.GOOGLE]: {},
        [CalendarProvider.OUTLOOK]: {},
    },
    onSelectedCalendarChange: () => {},
    onCalendarConnectionRemove: () => {},
    settingsModalRef: null,
});

const CalendarToolbar = (props: ToolbarProps<CalendarEvent, CalendarProps>) => {
    const contextProps = useContext(CalendarToolbarPropsContext);
    invariant(contextProps);
    const amplitudeService = AmplitudeService.getInstance();
    const calendarCount = useMemo(() => {
        let total = 0;
        for (const groupByProvider of Object.values(contextProps.calendars)) {
            for (const groupByEmail of Object.values(groupByProvider)) {
                for (const _calendar of Object.values(groupByEmail)) {
                    total++;
                }
            }
        }
        return total;
    }, [contextProps.calendars]);

    const handleOpenCalendarBtnClick = () => {
        amplitudeService.logEvent(new OpenCalendarLinkEvent());
        for (const byEmail of Object.values(contextProps.calendars)) {
            for (const byId of Object.values(byEmail)) {
                for (const calendar of Object.values(byId)) {
                    switch (calendar.provider) {
                        case CalendarProvider.OUTLOOK:
                            BrowserUtil.openPage("https://outlook.live.com/calendar/", true);
                            return;
                        case CalendarProvider.GOOGLE:
                            BrowserUtil.openPage("https://calendar.google.com/", true);
                            return;
                    }
                }
            }
        }
    };

    return (
        <ToolbarRow className={`toolbar-row-buttons`} isCondensed={props.view === "week"}>
            {props.view === "day" && (
                <span>
                    <DateText>{props.date.toLocaleString("default", { weekday: "short" })}</DateText>
                    <DateText>, </DateText>
                    <wbr />
                    <DateText>{props.date.toLocaleString("default", { month: "short", day: "numeric" })}</DateText>
                </span>
            )}

            <StyledChevronLeftIcon className={`calendar-date-left-icon`} onClick={() => props.onNavigate("PREV")} />

            <StyledChevronRightIcon className={`calendar-date-right-icon`} onClick={() => props.onNavigate("NEXT")} />

            <TodayButton
                className={`toolbar-today-button`}
                isLink={false}
                text={TODAY_BUTTON_TEXT}
                handleClick={() => props.onNavigate("TODAY")}
            />

            <CalendarSettingsModal
                onCalendarConnectionRemove={contextProps.onCalendarConnectionRemove}
                imperativeRef={contextProps.settingsModalRef}
            />

            {contextProps.calendars && (
                <CalendarSelector
                    key={`google-calendar-selector`}
                    className={`calendar-selector`}
                    selectedCalendarList={contextProps.selectedCalendarList}
                    onChange={contextProps.onSelectedCalendarChange}
                    openSettings={() => contextProps.settingsModalRef.current?.open()}
                    calendars={contextProps.calendars}
                />
            )}

            <ExternalLinkIcon className={`google-calendar-link-button`} onClick={handleOpenCalendarBtnClick} />

            {calendarCount === 0 && props.view === "week" && (
                <>
                    <GoogleCalendarAuthButton source={"week_view"} />
                    <OutlookCalendarAuthButton source={"week_view"} />
                </>
            )}
        </ToolbarRow>
    );
};
export default CalendarToolbar;
